/**
 * Unauthenticated public API client.
 * Used for customer-facing pages (online booking, payment, tips, secure
 * add-card links, etc.) that do not require a user session.
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { normalizeError } from './errors';
import { TIMEOUT_MS, injectTelemetryHeaders } from './client';

// Headers that would tie a public request to whatever session/tenant the
// browser happens to have. Public pages resolve their tenant + subject purely
// from a signed token or slug in the URL, so these must never ride along — a
// logged-in operator opening a customer link (e.g. an add-card link for a
// different tenant) must not influence where the request lands.
const SESSION_HEADERS = ['Authorization', 'x-api-key', 'x-tenant-id'] as const;

function stripSessionHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  for (const header of SESSION_HEADERS) {
    config.headers.delete(header);
  }
  return config;
}

export const publicClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_OPERATIONS_URL ?? '',
  timeout: TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

// Defense in depth: this client never sets auth headers itself, but strip any
// that slip in via axios global defaults or a per-call config so the public
// surface stays session-independent regardless of future regressions.
publicClient.interceptors.request.use(stripSessionHeaders, (error) =>
  Promise.reject(normalizeError(error)),
);
// Correlation headers only (`x-request-id`, `traceparent`): they identify the
// request, never the person, so the public surface stays session-independent.
publicClient.interceptors.request.use(injectTelemetryHeaders, (error) =>
  Promise.reject(normalizeError(error)),
);

publicClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeError(error)),
);

/**
 * Resolve a public API path to an absolute URL against the operations host
 * (`publicClient.defaults.baseURL`). Customer-facing pages need this for
 * links the browser hits directly — `<a href>`, `<iframe src>`, `window.open`
 * — which would otherwise resolve a bare `/api/...` path against the SPA
 * origin and fall through to the SPA 404 route. Pass-through for values that
 * are already absolute (`http(s)://…`). Trims any trailing slash on the base
 * so the join never produces a `//`.
 */
export function toPublicApiUrl(pathOrUrl: string): string;
export function toPublicApiUrl(pathOrUrl: string | undefined): string | undefined;
export function toPublicApiUrl(pathOrUrl: string | undefined): string | undefined {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = String(publicClient.defaults.baseURL ?? '').replace(/\/+$/, '');
  return `${base}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

// ─── Typed wrappers ───────────────────────────────────────────────────────────

export async function publicGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await publicClient.get<T>(url, { params });
  return response.data;
}

export async function publicPost<T, D = unknown>(url: string, data?: D): Promise<T> {
  const response = await publicClient.post<T>(url, data);
  return response.data;
}

export async function publicPut<T, D = unknown>(url: string, data?: D): Promise<T> {
  const response = await publicClient.put<T>(url, data);
  return response.data;
}

export async function publicDelete<T>(url: string): Promise<T> {
  const response = await publicClient.delete<T>(url);
  return response.data;
}
